import { useFlags, useLDClientError } from "launchdarkly-react-client-sdk"

/**
 * Whether the consumer app's routes should be mounted.
 *
 * `useFlags()` cannot tell "the flag is off" from "LaunchDarkly never answered": both
 * leave `consumerAppSignUp` absent from the flag set, i.e. `undefined`. So a bare
 * `{consumerAppSignUp && <Route/>}` unmounts every `/consumer` route for anyone whose
 * network blocks LaunchDarkly — ad blockers and DNS-filtering resolvers both do, including
 * ones pushed by managed-Chrome policy, which is how this was found. The member lands on
 * the login page (signed out) or PageNotFound (signed in) with nothing logged by the app,
 * indistinguishable from the feature not existing.
 *
 * Splitting the two cases: an explicit `false` keeps the dark launch dark, while a missing
 * value fails OPEN once `withLDProvider` reports an initialization error. Showing the
 * consumer app to someone who blocks LaunchDarkly beats silently deleting it.
 *
 * While initialization is still in flight there is no error yet and the flag is still
 * `undefined`, so this stays `false` and a dark-launched funnel never flashes into view.
 */
export function useConsumerRoutesEnabled(): boolean {
  const { consumerAppSignUp } = useFlags<{ consumerAppSignUp?: boolean }>()
  const launchDarklyInitError = useLDClientError()
  return consumerAppSignUp ?? launchDarklyInitError !== undefined
}
